import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import styles from "./MawaqeetGenerateTimes.module.scss";

const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface CoordinateMapProps {
  lat: number | null;
  lon: number | null;
  onCoordinateChange?: (lat: number, lon: number) => void;
}

const CoordinateMap: React.FC<CoordinateMapProps> = ({
  lat,
  lon,
  onCoordinateChange,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const onChangeRef = useRef(onCoordinateChange);

  onChangeRef.current = onCoordinateChange;

  const hasCoords =
    lat != null &&
    lon != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    !(lat === 0 && lon === 0) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180;

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [64.5, 11.5],
      zoom: 4,
      scrollWheelZoom: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      if (!onChangeRef.current) return;
      onChangeRef.current(e.latlng.lat, e.latlng.lng);
    });

    mapInstanceRef.current = map;

    // Leaflet trenger invalidateSize etter at container er synlig.
    const t = window.setTimeout(() => map.invalidateSize(), 80);

    return () => {
      window.clearTimeout(t);
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (!hasCoords) {
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
      return;
    }

    const position: L.LatLngExpression = [lat!, lon!];

    if (!markerRef.current) {
      markerRef.current = L.marker(position, {
        icon: defaultIcon,
        draggable: true,
        title: `${lat}, ${lon}`,
      }).addTo(map);

      markerRef.current.on("dragend", () => {
        const pos = markerRef.current?.getLatLng();
        if (!pos || !onChangeRef.current) return;
        onChangeRef.current(pos.lat, pos.lng);
      });
    } else {
      markerRef.current.setLatLng(position);
    }

    map.setView(position, Math.max(map.getZoom(), 12));
    window.setTimeout(() => map.invalidateSize(), 50);
  }, [hasCoords, lat, lon]);

  return (
    <div className={styles.mapCard}>
      <div className={styles.mapHeader}>
        <b className={styles.mapTitle}>Posisjon på kart</b>
        {hasCoords ? (
          <span className={styles.mapCoords}>
            {lat!.toFixed(6)}, {lon!.toFixed(6)}
          </span>
        ) : (
          <span className={styles.mapHint}>
            Søk etter by, lim inn lat,lon eller klikk på kartet
          </span>
        )}
      </div>
      <div ref={mapRef} className={styles.mapCanvas} />
      {hasCoords && (
        <p className={styles.mapFooter}>
          Dra nålen eller klikk på kartet for å justere nøyaktig posisjon.
        </p>
      )}
    </div>
  );
};

export default CoordinateMap;
